import {
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/frontend-plugin-api';
import {
  ALMAApi,
  Configuration,
  MainAskResponse,
  MainFeedbackRequest,
  MainFeedbackResponse,
} from './alma-sdk';
import {
  AlmaApi,
  AlmaApiResponse,
  MainLastConversationResponse,
  MainRecentConversationsResponse,
} from './types';

export class AlmaRestApi implements AlmaApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly oktaApi: OAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    oktaApi: OAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.oktaApi = options.oktaApi;
  }

  private async getAlmaApi() {
    const config = await this.getApiConfig();
    return new ALMAApi(config);
  }

  private async getApiConfig() {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const oktaToken = await this.oktaApi.getAccessToken();
    return new Configuration({
      basePath: `${baseUrl}/alma`,
      fetchApi: this.fetchApi.fetch,
      headers: {
        'Alma-Token': oktaToken,
      },
    });
  }

  async ask(
    message: string,
    conversationId?: number,
    pageContext?: string,
  ): Promise<AlmaApiResponse<MainAskResponse>> {
    try {
      const almaApi = await this.getAlmaApi();
      const result = await almaApi.askPostRaw({
        body: { message, conversationId, pageContext },
      });
      const value = await result.value();

      return {
        ok: result.raw.ok,
        value,
        statusCode: result.raw.status,
        message: result.raw.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async feedback(
    data: MainFeedbackRequest,
  ): Promise<AlmaApiResponse<MainFeedbackResponse>> {
    const { rating, description, question, answer, timestamp } = data;
    try {
      const almaApi = await this.getAlmaApi();
      const result = await almaApi.feedbackPostRaw({
        body: { rating, description, question, answer, timestamp },
      });
      const value = await result.value();

      return {
        ok: result.raw.ok,
        value,
        statusCode: result.raw.status,
        message: result.raw.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async getLastConversation(): Promise<
    AlmaApiResponse<MainLastConversationResponse>
  > {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      const oktaToken = await this.oktaApi.getAccessToken();
      const result = await this.fetchApi.fetch(
        `${baseUrl}/alma/last-conversation`,
        {
          method: 'GET',
          headers: {
            'Alma-Token': oktaToken,
          },
        },
      );
      const value = await result.json();

      return {
        ok: result.ok,
        value: {
          conversationId: value?.conversation_id ?? 0,
          messages: Array.isArray(value?.messages) ? value.messages : [],
        },
        statusCode: result.status,
        message: result.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async getRecentConversations(
    limit = 20,
  ): Promise<AlmaApiResponse<MainRecentConversationsResponse>> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      const oktaToken = await this.oktaApi.getAccessToken();
      const result = await this.fetchApi.fetch(
        `${baseUrl}/alma/conversations?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Alma-Token': oktaToken,
          },
        },
      );
      const value = await result.json();

      return {
        ok: result.ok,
        value: {
          conversations: Array.isArray(value?.conversations)
            ? value.conversations.map((c: any) => ({
                conversationId: c?.conversation_id ?? 0,
                title: c?.title ?? '',
                updatedAt: c?.updated_at ?? '',
              }))
            : [],
        },
        statusCode: result.status,
        message: result.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async getConversationById(
    conversationId: number,
  ): Promise<AlmaApiResponse<MainLastConversationResponse>> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      const oktaToken = await this.oktaApi.getAccessToken();
      const result = await this.fetchApi.fetch(
        `${baseUrl}/alma/conversations/${conversationId}`,
        {
          method: 'GET',
          headers: {
            'Alma-Token': oktaToken,
          },
        },
      );
      const value = await result.json();

      return {
        ok: result.ok,
        value: {
          conversationId: value?.conversation_id ?? 0,
          messages: Array.isArray(value?.messages) ? value.messages : [],
        },
        statusCode: result.status,
        message: result.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async deleteConversation(
    conversationId: number,
  ): Promise<AlmaApiResponse<{ status: string }>> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      const oktaToken = await this.oktaApi.getAccessToken();
      const result = await this.fetchApi.fetch(
        `${baseUrl}/alma/clear-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Alma-Token': oktaToken,
          },
          body: JSON.stringify({ conversation_id: conversationId }),
        },
      );
      const value = await result.json();
      return {
        ok: result.ok,
        value,
        statusCode: result.status,
        message: result.statusText,
      };
    } catch (error: any) {
      return {
        ok: false,
        value: undefined,
        statusCode: error?.response?.status ?? 400,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }
}
